import * as React from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { Row, Col, Button, Input, Spin, Icon, message } from "antd";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import axios from "axios";
import Bluebird from "bluebird";
import styled from "styled-components";
import * as _ from "lodash";

import {
  displayStringForExtraType,
  priceString,
  weekdayDayMonthDate,
} from "../../../lib/formatters";
import { RootState } from "../../../store/rootReducer";
import {
  updateOrder,
  resetOrder,
  createOrderAsync,
  updateSinglePoint,
  updateMultiPoints,
} from "../actions";
import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import RowWrapper from "../../../shared/RowWrapper";
import { CreateOrderRequest } from "../../../lib/model/CreateOrderRequest";
import { CreateAddressRequest } from "../../../lib/model/AddressState";
import { ApiClient } from "../../../lib/api";
import { SummaryEntry } from "../../bookDirect/components/SummaryEntry";
import { SelectedExtraRow } from "../../bookDirect/components/SelectedExtraRow";
import { PointSummaryComponent } from "./PointSummaryComponent";
import { TourType } from "../../../lib/model/TourType";
import { Order } from "../../../lib/model/Order";
import { removeItemAtIndex } from "../../../lib/helpers";
import { BookMultiOrder } from "../model/BookMultiOrder";
import {
  PICKUP_DOCUMENT,
  DROPOFF_DOCUMENT,
  INITIAL_PERCENT,
  StyledProgress,
  ProgressWrapper,
  formatProgressLabel,
} from "../../bookDirect/components/BookDirectSummaryContainer";
import { fetchOrdersAsync } from "../../orders/actions";
import TextArea from "antd/lib/input/TextArea";

const client = new ApiClient();

export const StyledInput = styled(Input)`
  height: 40px !important;
  background-color: #f2f2f2 !important;
  border: none !important;
  font-size: 11px !important;
  line-height: 15px !important;
  color: #386ee8 !important;
  font-weight: 300 !important;
  &::placeholder {
    color: #386ee8 !important;
    font-weight: 300 !important;
  }
`;

const StyledTextArea = styled(TextArea)`
  background-color: #f2f2f2 !important;
  border: none !important;
  font-size: 11px !important;
  line-height: 15px !important;
  color: #386ee8 !important;
  font-weight: 300 !important;
  padding: 12px !important;
  &::placeholder {
    color: #386ee8 !important;
    font-weight: 300 !important;
  }
`;

const Label = styled.span`
  color: #061a32;
  font-size: 11px;
  font-weight: 300;
  display: inline-block;
`;

const HintLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  display: inline-block;
  color: rgba(6, 26, 50, 0.7);
`;

const PriceLabel = styled.span`
  font-size: 28px;
  color: #386ee8;
`;

const isEmpty = (documents: IDocument<RcFile[]>) =>
  Object.entries(documents).length === 0 && documents.constructor === Object;

interface IDocument<TValue> {
  [id: string]: TValue;
}

type State = {
  isLoadingAddressCreate: boolean;
  pickupDocuments: IDocument<RcFile[]>;
  dropoffDocuments: IDocument<RcFile[]>;
  isUploading: boolean;
  uploadingProgress: number;
};

const mapStateToProps = (state: RootState) => ({
  order: state.bookMulti.order,
  quote: state.bookMulti.quote,
  isLoadingCreate: state.bookMulti.isLoadingCreate,
  createdOrder: state.bookMulti.createdOrder,
  addresses: state.addresses.addresses,
  openOrders: state.orders.openOrders,
  error: state.bookMulti.createError,
});

const dispatchProps = {
  push,
  updateOrder,
  resetOrder,
  updateSinglePoint,
  updateMultiPoints,
  createOrder: createOrderAsync.request,
  fetchOrders: fetchOrdersAsync.request,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

class BookMultiSummaryContainer extends React.Component<Props, State> {
  readonly state: State = {
    isLoadingAddressCreate: false,
    pickupDocuments: {},
    dropoffDocuments: {},
    isUploading: false,
    uploadingProgress: INITIAL_PERCENT,
  };
  render() {
    const {
      order,
      quote,
      updateOrder,
      updateSinglePoint,
      updateMultiPoints,
      isLoadingCreate,
      addresses,
    } = this.props;
    const { singlePoint, multiPoints } = order;
    const {
      isLoadingAddressCreate,
      isUploading,
      uploadingProgress,
      pickupDocuments,
      dropoffDocuments,
    } = this.state;
    const isDocumentAvailable =
      !isEmpty(pickupDocuments) || !isEmpty(dropoffDocuments);

    return (
      <Spin
        size="large"
        indicator={<Icon type="loading" spin />}
        spinning={
          (isLoadingCreate || isLoadingAddressCreate) && !isDocumentAvailable
        }
        tip="Auftrag wird erstellt..."
      >
        <MainComponentContainer>
          <RowWrapper type="flex">
            {/* DATE */}
            <Col span={24}>
              <Row type="flex">
                <Col span={11} style={{ paddingBottom: "18px" }}>
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Datum"
                    subTitle={!!order.date && weekdayDayMonthDate(order.date)}
                  ></SummaryEntry>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              {/* DISTRIBUTION */}
              {order.type === TourType.Distribution && (
                <>
                  <PointSummaryComponent
                    title="Beladung"
                    prefix="Abhol"
                    point={singlePoint}
                    onChange={updateSinglePoint}
                    loads={multiPoints.flatMap((m) => m.loads)}
                    onEditClick={this.handleEditClicked}
                    addresses={addresses}
                    handleDocumentBeforeUpload={(_, fileList: RcFile[]) =>
                      this.saveDocumentToLocal(PICKUP_DOCUMENT, fileList)
                    }
                    onRemoveDocument={(file: UploadFile) =>
                      this.deleteDocumentFromLocal(PICKUP_DOCUMENT, file)
                    }
                    documents={pickupDocuments[0]}
                  />
                  {multiPoints.map((point, index) => (
                    <PointSummaryComponent
                      title={`Entladung ${
                        multiPoints.length > 1 ? index + 1 : ""
                      }`}
                      prefix="Anliefer"
                      point={point}
                      onChange={(point) => updateMultiPoints(index, point)}
                      onEditClick={this.handleEditClicked}
                      addresses={addresses}
                      handleDocumentBeforeUpload={(_, fileList: RcFile[]) =>
                        this.saveDocumentToLocal(
                          DROPOFF_DOCUMENT,
                          fileList,
                          index
                        )
                      }
                      onRemoveDocument={(file: UploadFile) =>
                        this.deleteDocumentFromLocal(
                          DROPOFF_DOCUMENT,
                          file,
                          index
                        )
                      }
                      documents={dropoffDocuments[index]}
                    />
                  ))}
                </>
              )}
              {/* COLLECTION */}
              {order.type === TourType.Collection && (
                <>
                  {multiPoints.map((point, index) => (
                    <PointSummaryComponent
                      title={`Beladung ${
                        multiPoints.length > 1 ? index + 1 : ""
                      }`}
                      prefix="Abhol"
                      point={point}
                      onChange={(point) => updateMultiPoints(index, point)}
                      onEditClick={this.handleEditClicked}
                      addresses={addresses}
                      handleDocumentBeforeUpload={(_, fileList: RcFile[]) =>
                        this.saveDocumentToLocal(
                          PICKUP_DOCUMENT,
                          fileList,
                          index
                        )
                      }
                      onRemoveDocument={(file: UploadFile) =>
                        this.deleteDocumentFromLocal(
                          PICKUP_DOCUMENT,
                          file,
                          index
                        )
                      }
                      documents={pickupDocuments[index]}
                    />
                  ))}
                  <PointSummaryComponent
                    title="Entladung"
                    prefix="Anliefer"
                    point={singlePoint}
                    onChange={updateSinglePoint}
                    loads={multiPoints.flatMap((m) => m.loads)}
                    onEditClick={this.handleEditClicked}
                    addresses={addresses}
                    handleDocumentBeforeUpload={(_, fileList: RcFile[]) =>
                      this.saveDocumentToLocal(DROPOFF_DOCUMENT, fileList)
                    }
                    onRemoveDocument={(file: UploadFile) =>
                      this.deleteDocumentFromLocal(DROPOFF_DOCUMENT, file)
                    }
                    documents={dropoffDocuments[0]}
                  />
                </>
              )}
            </Col>

            <Col span={24} style={{ marginBottom: 10 }}>
              <SummaryEntry title="Abrechnung und Kommentar">
                <Row
                  type="flex"
                  gutter={35}
                  style={{ justifyContent: "start" }}
                >
                  <Col span={11} style={{ margin: "14px 0" }}>
                    <StyledTextArea
                      placeholder="Abrechnungsreferenz (optional)"
                      rows={3}
                      value={order.internalReference}
                      onChange={(value) =>
                        updateOrder({
                          ...order,
                          internalReference: value.currentTarget.value,
                        })
                      }
                    />
                    <HintLabel>
                      Diese Information erscheint auf Ihrer Rechnung.
                    </HintLabel>
                  </Col>
                  <Col span={12} style={{ margin: "14px 0" }}>
                    <StyledTextArea
                      placeholder="Auftragskommentar (optional)"
                      rows={3}
                      value={order.comment}
                      onChange={(value) =>
                        updateOrder({
                          ...order,
                          comment: value.currentTarget.value,
                        })
                      }
                    />
                  </Col>
                </Row>
              </SummaryEntry>
            </Col>

            {/* EXTRAS AND PRICE */}

            <Col span={24}>
              <Row type="flex" gutter={29}>
                <Col span={11}>
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Fahrzeugoptionen"
                  >
                    {order.extras.length > 0 ? (
                      order.extras.map((e, index) => (
                        <SelectedExtraRow
                          key={`ExtraRow${index}`}
                          title={displayStringForExtraType(e)}
                        />
                      ))
                    ) : (
                      <Label>keine</Label>
                    )}
                  </SummaryEntry>
                </Col>

                <Col span={13}>
                  <SummaryEntry title="Festpreis">
                    <Row type="flex" justify="space-between">
                      <Col span="auto">
                        <PriceLabel>
                          {quote ? priceString(quote.fullCost.net) : "- €"}
                        </PriceLabel>
                      </Col>
                      <Col span="auto">
                        <Button
                          type="primary"
                          disabled={true}
                          onClick={this.handleBookingClicked}
                          style={{
                            backgroundColor: "#386ee8 !important",
                            border: "none !important",
                            fontWeight: 300,
                            borderRadius: 0,
                            height: "36px",
                          }}
                        >
                          Jetzt kostenpflichtig buchen
                        </Button>
                      </Col>
                    </Row>
                    <Row type="flex">
                      <Col span={24} style={{ marginTop: 10 }}>
                        <HintLabel>
                          {`Inkl. ${
                            quote && quote.includedWaitingTime
                          } Minuten Wartezeit. Weitere Informationen in unseren `}
                          <a
                            href="https://forward-cargo.com/terms#waiting-times"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            AGB
                          </a>
                          . Unsere AGB und die ADSp 2017 finden Anwendung.
                        </HintLabel>
                      </Col>
                    </Row>
                  </SummaryEntry>
                </Col>
              </Row>
            </Col>
          </RowWrapper>
        </MainComponentContainer>
        {isUploading && isDocumentAvailable && (
          <ProgressWrapper>
            <StyledProgress
              type="circle"
              percent={uploadingProgress}
              format={formatProgressLabel}
            />
          </ProgressWrapper>
        )}
      </Spin>
    );
  }

  componentDidUpdate = (prevProps: Props, prevState: {}, snapshot: any) => {
    if (!prevProps.createdOrder && this.props.createdOrder) {
      this.uploadDocuments(this.props.createdOrder);
    }
    if (!prevProps.error && this.props.error) {
      message.error(this.props.error.message);
    }
  };

  handleEditClicked = () => {
    this.props.push("/multi");
  };

  handleBookingClicked = async () => {
    try {
      const enhancedOrder = await this.ensureAddressesCreated();

      this.props.createOrder(
        CreateOrderRequest.requestForMultiOrder(enhancedOrder)
      );
    } catch (error) {
      message.error(error.message);
    }
  };

  ensureAddressesCreated = async (): Promise<BookMultiOrder> => {
    this.setState({ isUploading: true, isLoadingAddressCreate: true });
    const { order } = this.props;
    const promises = [
      order.singlePoint.address,
      ...order.multiPoints.map((mp) => mp.address),
    ].map((a) => {
      if (a.id) return a.id;
      return client
        .createAddress(new CreateAddressRequest(a.create!))
        .then((a) => a.id);
    });

    const ids = await Promise.all(promises);
    const enhancedSinglePoint = {
      ...order.singlePoint,
      address: { id: ids[0] },
    };
    const multiIds = removeItemAtIndex([...ids], 0);
    const enhancedMultiPoints = _.zip(multiIds, order.multiPoints).map((z) => ({
      ...z[1]!,
      address: { id: z[0]! },
    }));
    const enhancedOrder = {
      ...order,
      singlePoint: enhancedSinglePoint,
      multiPoints: enhancedMultiPoints,
    };
    this.setState({ isLoadingAddressCreate: false });
    return enhancedOrder;
  };

  saveDocumentToLocal = (
    documentType: string,
    fileList: RcFile[],
    pointNumber: number = 0
  ) => {
    const { order } = this.props;
    const { pickupDocuments, dropoffDocuments } = this.state;

    if (documentType === PICKUP_DOCUMENT) {
      const newPickupDocuments = { ...pickupDocuments };
      const index = order.type === TourType.Distribution ? 0 : pointNumber;
      if (!newPickupDocuments[index]) {
        newPickupDocuments[index] = [];
      }

      newPickupDocuments[index] = newPickupDocuments[index].concat(fileList);

      this.setState({ pickupDocuments: { ...newPickupDocuments } });
    } else {
      const newDropoffDocuments = { ...dropoffDocuments };
      const index = order.type === TourType.Distribution ? pointNumber : 0;
      if (!dropoffDocuments[index]) {
        newDropoffDocuments[index] = [];
      }
      newDropoffDocuments[index] = newDropoffDocuments[index].concat(fileList);

      this.setState({ dropoffDocuments: { ...newDropoffDocuments } });
    }

    return false;
  };

  deleteDocumentFromLocal = (
    documentType: string,
    file: UploadFile,
    pointNumber: number = 0
  ) => {
    const { order } = this.props;
    const { pickupDocuments, dropoffDocuments } = this.state;

    if (documentType === PICKUP_DOCUMENT) {
      const newPickupDocuments = { ...pickupDocuments };
      const index = order.type === TourType.Distribution ? 0 : pointNumber;
      const newFileList = newPickupDocuments[index].filter(
        (document) => document.uid !== file.uid
      );
      newPickupDocuments[index] = newFileList;
      this.setState({ pickupDocuments: { ...newPickupDocuments } });
    } else {
      const newDropoffDocuments = { ...dropoffDocuments };
      const index = order.type === TourType.Distribution ? pointNumber : 0;
      const newFileList = newDropoffDocuments[index].filter(
        (document) => document.uid !== file.uid
      );
      newDropoffDocuments[index] = newFileList;
      this.setState({ dropoffDocuments: { ...newDropoffDocuments } });
    }
  };

  // upload file and calculate the progress
  uploadFile = (url: string, file: RcFile) => {
    const { pickupDocuments, dropoffDocuments, uploadingProgress } = this.state;
    const _this = this;
    let documentCounts = 0;
    Object.keys(pickupDocuments).forEach(function (key) {
      pickupDocuments[key].forEach(() => (documentCounts += 1));
    });
    Object.keys(dropoffDocuments).forEach(function (key) {
      dropoffDocuments[key].forEach(() => (documentCounts += 1));
    });

    return axios.put(url, file, {
      onUploadProgress: ({ total, loaded }) => {
        _this.setState({
          uploadingProgress:
            uploadingProgress +
            parseInt(
              Math.round(
                ((100 - INITIAL_PERCENT) / documentCounts) * (loaded / total)
              ).toFixed(2)
            ),
        });
      },
    });
  };

  // get the signed url for the document upload
  getSignedUrl = (orderId: string, fileExtension: string): Promise<string> => {
    return client
      .getStopDocumentUploadUrl(orderId, fileExtension)
      .then((r) => r.url);
  };

  // get the file extension from the upload file
  getFileExtension = (file: RcFile): string => {
    const fileName = file.name;
    return fileName.substr(fileName.lastIndexOf(".") + 1);
  };

  // Implement the document upload process. Can be improved later for the code optimization
  uploadDocuments = async (order: Order): Promise<void> => {
    const { pickupDocuments, dropoffDocuments } = this.state;
    const { id, shipmentIds } = order;
    const _this = this;
    let signedUrlsForPickupDocs: IDocument<string[]> = {},
      signedUrlsForDropoffDocs: IDocument<string[]> = {};

    if (!isEmpty(pickupDocuments)) {
      // generate the signed urls for the pickup documents in order of precedence
      await Promise.all(
        Object.keys(pickupDocuments).map(async function (key, index) {
          const documents = pickupDocuments[key];

          await Bluebird.mapSeries(documents, function (document) {
            const fileExtension = _this.getFileExtension(document);
            return _this.getSignedUrl(id, fileExtension);
          }).then(function (urls) {
            signedUrlsForPickupDocs[key] = urls;
          });
        })
      );

      // upload the pickup documents in order of precedence
      await Promise.all(
        Object.keys(pickupDocuments).map(async function (key, index) {
          const documents = pickupDocuments[key];

          await Bluebird.mapSeries(documents, function (doc, index) {
            return _this.uploadFile(signedUrlsForPickupDocs[key][index], doc);
          }).then(function () {
            console.log("PickupDocuments uploading is done");
          });
        })
      );
    }

    if (!isEmpty(dropoffDocuments)) {
      // generate the signed urls for the dropoff documents in order of precedence
      await Promise.all(
        Object.keys(dropoffDocuments).map(async function (key, index) {
          const documents = dropoffDocuments[key];

          await Bluebird.mapSeries(documents, function (document) {
            const fileExtension = _this.getFileExtension(document);
            return _this.getSignedUrl(id, fileExtension);
          }).then(function (urls) {
            signedUrlsForDropoffDocs[key] = urls;
          });
        })
      );

      // upload the dropoff documents in order of precedence
      await Promise.all(
        Object.keys(dropoffDocuments).map(async function (key, index) {
          const documents = dropoffDocuments[key];

          await Bluebird.mapSeries(documents, function (doc, index) {
            return _this.uploadFile(signedUrlsForDropoffDocs[key][index], doc);
          }).then(function () {
            console.log("DropoffDocuments uploading is done");
          });
        })
      );
    }

    // parse the signed urls to exclude the query params
    let parsedPickupDocLinks: IDocument<string[]> = {},
      parsedDropoffDocLinks: IDocument<string[]> = {};

    Object.keys(signedUrlsForPickupDocs).forEach((key) => {
      const urls = signedUrlsForPickupDocs[key].map((url) => url.split("?")[0]);
      parsedPickupDocLinks[key] = urls;
    });

    Object.keys(signedUrlsForDropoffDocs).forEach((key) => {
      const urls = signedUrlsForDropoffDocs[key].map(
        (url) => url.split("?")[0]
      );
      parsedDropoffDocLinks[key] = urls;
    });

    // upload the documents if the documents are uploaded in the local
    if (
      shipmentIds &&
      (!isEmpty(pickupDocuments) || !isEmpty(dropoffDocuments))
    ) {
      await Promise.all(
        shipmentIds.map(async (shipmentId, index) => {
          const pickupDocLinks =
            this.props.order.type === TourType.Distribution
              ? parsedPickupDocLinks[0]
              : parsedPickupDocLinks[index];
          const dropoffDocLinks =
            this.props.order.type === TourType.Distribution
              ? parsedDropoffDocLinks[index]
              : parsedDropoffDocLinks[0];
          return await client.updateDocuments(
            shipmentId,
            pickupDocLinks || [],
            dropoffDocLinks || []
          );
        })
      );
    }

    this.setState({ isUploading: false });

    // order creation successfull

    message.success("Auftrag erfolgreich erstellt.");

    this.props.push("/orders");
    this.props.resetOrder();
  };
}

export default connect(
  mapStateToProps,
  dispatchProps
)(BookMultiSummaryContainer);
